import React from "react";
import { motion } from "framer-motion";
import PageShell from "../components/layout/PageShell";
import CinematicHero from "../components/layout/CinematicHero";
import Container from "../components/primitives/Container";
import Section from "../components/primitives/Section";
import Eyebrow from "../components/primitives/Eyebrow";
import Reveal from "../components/primitives/Reveal";
import NumberedItem from "../components/editorial/NumberedItem";
import ClosingStrip from "../components/editorial/ClosingStrip";
import SeoHead from "../components/seo/SeoHead";
import { EASE, prefersReducedMotion } from "../lib/motion";
import { IMAGES } from "../content/images";
import { FIRM } from "../content/site";

const SectionDisplay = ({ children, className = "" }) => (
  <h2
    className={`font-serif font-normal text-navy text-balance ${className}`}
    style={{ fontSize: "clamp(36px, 4.6vw, 64px)", lineHeight: 1.08, letterSpacing: "-0.01em" }}
  >
    {children}
  </h2>
);

/**
 * FirmHeroOverlay — editorial overlay for the Firm cinematic hero.
 * Eyebrow: "The Firm"
 * Title:   "The Firm."
 * Statement: "A note on what we are — and what we are not."
 * Staggered mount reveal aligned with the CinematicHero image fade.
 */
const FirmHeroOverlay = () => {
  const reduce = prefersReducedMotion();
  const t = {
    eyebrow: reduce ? { duration: 0 } : { duration: 1.0, ease: EASE, delay: 1.4 },
    title: reduce ? { duration: 0 } : { duration: 1.0, ease: EASE, delay: 1.58 },
    statement: reduce ? { duration: 0 } : { duration: 1.0, ease: EASE, delay: 1.86 },
  };
  return (
    <>
      <motion.div
        className="flex items-center gap-4"
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={t.eyebrow}
      >
        <span aria-hidden="true" className="block h-px bg-gold" style={{ width: 24 }} />
        <span
          className="font-sans uppercase text-gold"
          style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
        >
          The Firm
        </span>
      </motion.div>

      <motion.h1
        className="mt-8 font-serif font-normal text-offwhite text-balance"
        initial={reduce ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={t.title}
        style={{
          fontSize: "clamp(44px, 7vw, 108px)",
          lineHeight: 1.02,
          letterSpacing: "-0.02em",
          textShadow: "0 1px 24px rgba(11,19,43,0.35)",
        }}
      >
        The Firm.
      </motion.h1>

      <motion.p
        className="mt-8 max-w-[52ch] font-serif italic text-offwhite/90"
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={t.statement}
        style={{
          fontSize: "clamp(20px, 1.9vw, 28px)",
          lineHeight: 1.35,
          textShadow: "0 1px 16px rgba(11,19,43,0.30)",
        }}
      >
        A note on what we are &mdash; and what we are not.
      </motion.p>
    </>
  );
};

const Firm = () => {
  return (
    <PageShell>
      <SeoHead
        title="The Firm"
        description="A privately held investment institution that acquires, develops, and stewards businesses across industrials, infrastructure, energy, and manufacturing."
        path="/firm"
        ogImage={IMAGES.firmBoardroom}
      />

      <CinematicHero
        image={IMAGES.firmBoardroom}
        imageAlt="Executive boardroom at dusk overlooking the city — the private counsel room of the firm."
        eager
        data-testid="firm-hero"
      >
        <FirmHeroOverlay />
      </CinematicHero>

      {/* Intro — moved below the cinematic hero */}
      <Section tone="light" className="pt-20 md:pt-24 lg:pt-28 pb-6 md:pb-8 lg:pb-8" data-testid="firm-intro">
        <Container>
          <Reveal className="max-w-3xl">
            <Eyebrow tone="muted">A note on what we are</Eyebrow>
            <p
              className="mt-10 font-serif font-normal text-navy text-balance"
              style={{ fontSize: "clamp(28px, 3.4vw, 44px)", lineHeight: 1.25 }}
            >
              {FIRM.intro}
            </p>
          </Reveal>
        </Container>
      </Section>

      {/* Mission / Vision */}
      <Section tone="offwhite" className="pt-6 md:pt-8 lg:pt-8 pb-6 md:pb-8 lg:pb-8" data-testid="firm-mission-vision">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-32">
            {[FIRM.mission, FIRM.vision].map((block) => (
              <Reveal key={block.eyebrow}>
                <Eyebrow tone="gold">{block.eyebrow}</Eyebrow>
                <SectionDisplay className="mt-8">{block.heading}</SectionDisplay>
                <span aria-hidden="true" className="mt-10 block h-px w-10 bg-gold" />
                <p className="mt-10 font-sans text-[16px] sm:text-[17px] leading-[1.75] text-navy/80 max-w-prose">
                  {block.body}
                </p>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* Values */}
      <Section tone="light" className="pt-6 md:pt-8 lg:pt-8 pb-20 md:pb-24 lg:pb-28" data-testid="firm-values">
        <Container>
          <Reveal className="max-w-2xl">
            <Eyebrow tone="gold">{FIRM.values.eyebrow}</Eyebrow>
            <SectionDisplay className="mt-8">{FIRM.values.heading}</SectionDisplay>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
            {FIRM.values.items.map((v, i) => (
              <Reveal key={v.name} delay={i * 60}>
                <NumberedItem index={i + 1} name={v.name} body={v.body} />
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <ClosingStrip>{FIRM.closing}</ClosingStrip>
    </PageShell>
  );
};

export default Firm;
